// Minimal RFC4180-ish CSV parser. Handles quoted fields, "" escapes, embedded
// commas/newlines, CRLF/LF, BOM, and auto-detects comma vs semicolon vs tab
// (Italian Excel exports often use ";"). No dependency.
//
// `lines[i]` is the 1-based PHYSICAL file line where data row `i` begins, so the
// UI can report errors against the user's actual spreadsheet line (blank lines
// and multi-line quoted cells are accounted for).

export type ParsedCsv = { columns: string[]; rows: string[][]; lines: number[] };

function detectDelimiter(sample: string): string {
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? "";
  const counts: Array<[string, number]> = [
    [",", (firstLine.match(/,/g) ?? []).length],
    [";", (firstLine.match(/;/g) ?? []).length],
    ["\t", (firstLine.match(/\t/g) ?? []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0]![1] > 0 ? counts[0]![0] : ",";
}

export function parseCsv(text: string): ParsedCsv {
  const src = text.replace(/^﻿/, ""); // strip UTF-8 BOM
  const delim = detectDelimiter(src);
  const records: Array<{ cells: string[]; line: number }> = [];
  let field = "";
  let record: string[] = [];
  let inQuotes = false;
  let fieldStart = true; // are we at the first char of the current field?
  let line = 1;
  let recordLine = 1; // physical line where the current record began

  const endRecord = () => {
    record.push(field); field = "";
    if (!(record.length === 1 && record[0] === "")) records.push({ cells: record, line: recordLine });
    record = [];
  };

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else { if (ch === "\n") line++; field += ch; }
      continue;
    }
    // A quote is only special at the START of a field; elsewhere it is literal.
    if (ch === '"' && fieldStart) { inQuotes = true; fieldStart = false; continue; }
    if (ch === delim) { record.push(field); field = ""; fieldStart = true; continue; }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      endRecord();
      line++;
      recordLine = line;
      fieldStart = true;
      continue;
    }
    field += ch; fieldStart = false;
  }
  // flush trailing field/record
  if (field !== "" || record.length > 0) endRecord();

  if (records.length === 0) return { columns: [], rows: [], lines: [] };
  const columns = records[0]!.cells.map((c) => c.trim());
  const width = columns.length;
  const data = records.slice(1);
  const rows = data.map((rec) => {
    const out = rec.cells.slice(0, width);
    while (out.length < width) out.push("");
    return out;
  });
  return { columns, rows, lines: data.map((rec) => rec.line) };
}
